import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import { ServiceError } from "@core/exceptions"
import type {
    SubscriptionOfferItem,
    SubscriptionModuleItem,
    SubscriptionOffersResponse,
} from "@modules/abonnement/types/subscription"

// =====================
// Type definitions
// =====================

/**
 * Normalise la réponse GET /stripe/offers : l’API métier renvoie des tableaux ;
 * le store d’inscription attend des enregistrements indexés par nom d’offre / tag module.
 */
export function normalizeStripeOffersResponse(
    data: Record<string, unknown>
): SubscriptionOffersResponse {
    const rawOffers = data?.offers
    const rawModules = data?.modules
    const offers: NonNullable<SubscriptionOffersResponse["offers"]> = {}
    const modules: NonNullable<SubscriptionOffersResponse["modules"]> = {}

    if (Array.isArray(rawOffers)) {
        for (const el of rawOffers) {
            if (!el || typeof el !== "object") continue
            const o = el as Record<string, unknown>
            if (typeof o.name !== "string") continue
            const { name, ...rest } = o as { name: string } & Record<
                string,
                unknown
            >
            offers[name] = rest as SubscriptionOfferItem
        }
    } else if (
        rawOffers &&
        typeof rawOffers === "object" &&
        !Array.isArray(rawOffers)
    ) {
        Object.assign(
            offers,
            rawOffers as Record<string, SubscriptionOfferItem>
        )
    }

    if (Array.isArray(rawModules)) {
        for (const el of rawModules) {
            if (!el || typeof el !== "object") continue
            const m = el as Record<string, unknown>
            if (typeof m.tag !== "string") continue
            const { tag, ...rest } = m as { tag: string } & Record<
                string,
                unknown
            >
            modules[tag] = rest as SubscriptionModuleItem
        }
    } else if (
        rawModules &&
        typeof rawModules === "object" &&
        !Array.isArray(rawModules)
    ) {
        Object.assign(
            modules,
            rawModules as Record<string, SubscriptionModuleItem>
        )
    }

    return { offers, modules }
}

/** Payload pour la redirection vers le paiement Stripe */
export type RedirectToPaymentParams = {
    planId: string
    /** Ids des prix Stripe des modules, au format attendu par l'API : [{ id: "price_xxx" }, ...] */
    modulesId?: { id: string }[]
    user?: Record<string, unknown>
    company?: Record<string, unknown>
    pipedrive?: { organisationId?: number; dealId?: number; contactId?: number }
}

/** Réponse checkout Stripe (URL de redirection) */
export type CheckoutResponse = {
    checkout_url?: string
}

// =====================
// Service
// =====================

/**
 * Service Stripe pour le parcours d'inscription (offres, checkout, vérification utilisateur).
 */
export class StripeService {
    /**
     * Récupère les offres Stripe Homeys.
     * GET /api/metier/v5/stripe/offers
     * @returns Offres et modules formatés pour l'application
     */
    async getStripeOffers(): Promise<SubscriptionOffersResponse> {
        return handleServiceCall<SubscriptionOffersResponse>(async () => {
            const { data } = await axios.get<Record<string, unknown>>(
                "/api/metier/v5/stripe/offers",
                {
                    headers: {
                        "x-no-authorization": true,
                    },
                }
            )
            return normalizeStripeOffersResponse(data ?? {})
        })
    }

    /**
     * Crée une session checkout et retourne l'URL de redirection Stripe.
     * POST /api/metier/v5/stripe/payment/checkout
     * @param params - planId, modulesId, user, company, pipedrive
     * @returns URL de la page de paiement Stripe
     */
    async redirectToPayment(params: RedirectToPaymentParams): Promise<string> {
        return handleServiceCall<string>(async () => {
            const { data } = await axios.post<CheckoutResponse>(
                "/api/metier/v5/stripe/payment/checkout",
                {
                    priceId: params.planId,
                    modulesId: params.modulesId ?? [],
                    user: params.user,
                    company: params.company,
                    pipedrive: params.pipedrive,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "x-no-authorization": true,
                    },
                }
            )
            const url = data?.checkout_url
            if (!url) {
                throw new ServiceError(
                    {
                        message: "URL de paiement non retournée",
                        code: "error_unknown",
                    },
                    500
                )
            }
            return url
        })
    }

    /**
     * Vérifie qu'un utilisateur existe déjà avec cette adresse email.
     * GET /api/metier/v5/auth/verify
     * @param params - username (email)
     * @returns true si l'utilisateur existe, false si 404 (compte inexistant)
     */
    async verifyUserAccountForPayment(params: {
        username: string
    }): Promise<boolean> {
        return handleServiceCall<boolean>(async () => {
            await axios.get("/api/metier/v5/auth/verify", {
                params: { username: params.username },
                headers: {
                    "x-no-authorization": true,
                },
            })
            return true
        })
    }
}
